import React, { useCallback, useEffect, useRef, useState } from 'react';
import { socialStapelApi, type SocialAutoStand, type SocialPlattform, type SocialStapelJob } from '../services/api';
import { PLATTFORM_NAME, PLATTFORMEN } from './plattformen';
import './SocialStapel.css';

/**
 * Fassungen fuer Instagram, X und Facebook fuer viele Beitragsideen auf
 * einmal (Backend: POST /api/social-posts/stapel). Der Dialog startet einen
 * Durchlauf und fragt alle drei Sekunden den Stand ab. Laeuft im Mandanten
 * schon einer, zeigt er dessen Stand statt eines neuen Starts.
 */

const KURZ: Record<SocialPlattform, string> = { instagram: 'IG', x: 'X', facebook: 'FB' };

/** Kleine Plaketten IG / X / FB fuer die Plattformen, fuer die es schon Fassungen gibt. */
export const FassungsPlaketten: React.FC<{ plattformen?: SocialPlattform[] | null }> = ({ plattformen }) => {
  if (!plattformen || plattformen.length === 0) return null;
  const namen = plattformen.map((p) => PLATTFORM_NAME[p]).join(', ');
  return (
    <span className="social-plaketten" role="img" aria-label={`Fassungen vorhanden: ${namen}`} title={`Fassungen vorhanden: ${namen}`}>
      {plattformen.map((p) => (
        <span key={p} className={`social-plakette social-plakette--${p}`} aria-hidden="true">{KURZ[p]}</span>
      ))}
    </span>
  );
};

/**
 * Stand der automatischen Fassungen (nach dem Speichern von
 * LinkedIn-Varianten und nach jedem Start): "Automatisch: x von y erzeugt"
 * und, falls die KI nicht nutzbar war, deren Meldung; dazu Kombinationen,
 * die dauerhaft scheiterten (beim Start nicht mehr nachgeholt). Sonst nichts.
 */
export const AutoFassungenStand: React.FC<{ auto?: SocialAutoStand | null }> = ({ auto }) => {
  const dauerhaft = auto?.dauerhaftFehlgeschlagen ?? 0;
  if (!auto || (auto.gesamt === 0 && !auto.kontofehler && !auto.laufend && dauerhaft === 0)) return null;
  const runde = auto.gesamt > 0 || auto.laufend;
  const fehler = auto.fehler > 0 ? `, ${auto.fehler} fehlgeschlagen` : '';
  const liste = (auto.dauerhaft ?? [])
    .map((d) => `„${d.titel}“ (${PLATTFORM_NAME[d.platform] ?? d.platform}, ${d.versuche}×)${d.letzterFehler ? `: ${d.letzterFehler}` : ''}`)
    .join('\n');
  return (
    <div className="social-auto">
      {runde && (
        <p className="text-sm text-secondary social-auto__zeile" role="status" title={auto.letzterFehler || undefined}>
          Automatisch: <strong>{auto.fertig}</strong> von <strong>{auto.gesamt}</strong> erzeugt{fehler}
          {auto.laufend
            ? ` – läuft${auto.aktuell ? ` („${auto.aktuell.titel}“, ${PLATTFORM_NAME[auto.aktuell.platform]})` : ''}…`
            : '.'}
        </p>
      )}
      {dauerhaft > 0 && (
        <p className="text-sm text-secondary social-auto__dauerhaft" title={liste || undefined}>
          {dauerhaft} {dauerhaft === 1 ? 'Fassung' : 'Fassungen'} dauerhaft fehlgeschlagen – über den Stapel-Knopf erneut versuchen.
        </p>
      )}
      {auto.kontofehler && (
        <div className="alert alert-error" role="alert">
          Automatische Fassungen angehalten: {auto.kontofehler}
          {auto.verworfen > 0 ? ` (${auto.verworfen} Schritte ausgelassen; neuer Versuch beim nächsten Speichern von LinkedIn-Varianten oder Neustart.)` : ''}
        </div>
      )}
    </div>
  );
};

interface Props {
  /** Stand der automatischen Fassungen (fragt die Seite ab). */
  auto?: SocialAutoStand | null;
  /** Alle Ideen dieses Wochenplans (nach Termin). */
  editorialPlanId?: string;
  /** Oder ausgewaehlte Ideen. */
  postIdeaIds?: string[];
  /** Wofuer, z. B. der Planname -- steht im Dialog. */
  bezeichnung: string;
  onClose: () => void;
  /** Ein Durchlauf ist zu Ende (fertig, abgebrochen, Kontofehler). */
  onFertig?: (job: SocialStapelJob) => void;
  /** Abfrageabstand; nur Tests setzen ihn kleiner. */
  intervallMs?: number;
}

const fehlerText = (err: any, standard: string): string => err?.response?.data?.error || standard;

export const SocialStapelDialog: React.FC<Props> = ({ auto, editorialPlanId, postIdeaIds, bezeichnung, onClose, onFertig, intervallMs = 3000 }) => {
  const [plattformen, setPlattformen] = useState<SocialPlattform[]>([...PLATTFORMEN]);
  const [varianten, setVarianten] = useState(2);
  const [nurFehlende, setNurFehlende] = useState(true);
  const [job, setJob] = useState<SocialStapelJob | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [startet, setStartet] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  const gemeldet = useRef<string | null>(null);
  const onFertigRef = useRef(onFertig);
  onFertigRef.current = onFertig;

  // Laeuft im Mandanten schon ein Durchlauf? Dann dessen Stand zeigen.
  useEffect(() => {
    let aktiv = true;
    socialStapelApi.letzter()
      .then((letzter) => { if (aktiv && letzter?.status === 'laufend') setJob(letzter); })
      .catch(() => { /* ohne Stand: Formular zeigen */ })
      .finally(() => { if (aktiv) setLaedt(false); });
    return () => { aktiv = false; };
  }, []);

  const laufend = job?.status === 'laufend';

  // Stand abfragen, solange der Durchlauf laeuft.
  useEffect(() => {
    if (!job || job.status !== 'laufend') return undefined;
    const t = setTimeout(async () => {
      try {
        setJob(await socialStapelApi.stand(job.id));
      } catch (err: any) {
        setFehler(fehlerText(err, 'Der Stand konnte nicht abgefragt werden.'));
        // Weiter versuchen: ein Neuladen derselben Daten loest den naechsten Abruf aus.
        setJob((j) => (j ? { ...j } : j));
      }
    }, intervallMs);
    return () => clearTimeout(t);
  }, [job, intervallMs]);

  // Einmal melden, wenn ein Durchlauf zu Ende ist.
  useEffect(() => {
    if (job && job.status !== 'laufend' && gemeldet.current !== job.id) {
      gemeldet.current = job.id;
      onFertigRef.current?.(job);
    }
  }, [job]);

  const umschalten = (p: SocialPlattform) => {
    setPlattformen((liste) => (liste.includes(p) ? liste.filter((x) => x !== p) : PLATTFORMEN.filter((x) => x === p || liste.includes(x))));
  };

  const starten = useCallback(async () => {
    setFehler('');
    setHinweis('');
    setStartet(true);
    try {
      const antwort = await socialStapelApi.starten({ editorialPlanId, postIdeaIds, platforms: plattformen, variants: varianten, nurFehlende });
      gemeldet.current = null;
      setJob(antwort.job);
    } catch (err: any) {
      const laufendeId = err?.response?.status === 409 ? err.response.data?.jobId : null;
      if (typeof laufendeId === 'string') {
        setHinweis(fehlerText(err, 'Es läuft bereits ein Durchlauf.'));
        try {
          setJob(await socialStapelApi.stand(laufendeId));
        } catch {
          /* Stand unbekannt: Hinweis reicht */
        }
      } else {
        setFehler(fehlerText(err, 'Der Durchlauf konnte nicht gestartet werden.'));
      }
    } finally {
      setStartet(false);
    }
  }, [editorialPlanId, postIdeaIds, plattformen, varianten, nurFehlende]);

  const abbrechen = async () => {
    if (!job) return;
    try {
      setJob(await socialStapelApi.abbrechen(job.id));
    } catch (err: any) {
      setFehler(fehlerText(err, 'Der Durchlauf konnte nicht abgebrochen werden.'));
    }
  };

  const anteil = job && job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="modal-overlay">
      <div className="modal social-stapel" style={{ maxWidth: 640 }} role="dialog" aria-modal="true" aria-labelledby="social-stapel-titel">
        <div className="modal-header">
          <h2 className="modal-title" id="social-stapel-titel">Fassungen für Instagram / X / Facebook</h2>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Schließen">×</button>
        </div>
        <p className="text-sm text-secondary">
          Für <strong>{bezeichnung}</strong>. Je Beitragsidee und Plattform erzeugt die KI Varianten und speichert sie an der Idee;
          ausgewählt wird nichts – das entscheidest du im Studio der Idee.
        </p>

        <AutoFassungenStand auto={auto} />
        {hinweis && <div className="alert alert-info" role="status">{hinweis}</div>}
        {fehler && <div className="alert alert-error" role="alert">{fehler}</div>}

        {laedt ? (
          <p className="text-muted">Stand wird geladen…</p>
        ) : !job ? (
          <>
            <fieldset className="form-group social-stapel__plattformen">
              <legend className="form-label">Plattformen</legend>
              {PLATTFORMEN.map((p) => (
                <label key={p} className="social-stapel__wahl">
                  <input type="checkbox" checked={plattformen.includes(p)} onChange={() => umschalten(p)} /> {PLATTFORM_NAME[p]}
                </label>
              ))}
            </fieldset>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="social-stapel-varianten">Varianten je Plattform</label>
                <select id="social-stapel-varianten" className="form-select" value={varianten} onChange={(e) => setVarianten(Number(e.target.value))}>
                  {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="social-stapel__wahl">
                  <input type="checkbox" checked={nurFehlende} onChange={(e) => setNurFehlende(e.target.checked)} />
                  {' '}Nur fehlende (Ideen mit vorhandener Fassung der Plattform überspringen)
                </label>
              </div>
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-success" onClick={starten} disabled={startet || plattformen.length === 0}>
                {startet ? 'Wird gestartet…' : 'Erzeugen starten'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            </div>
          </>
        ) : (
          <div className="social-stapel__stand">
            <progress max={Math.max(job.total, 1)} value={job.done} aria-label="Fortschritt der Fassungen" style={{ width: '100%' }} />
            <p className="text-sm">
              <strong>{job.done}</strong> von <strong>{job.total}</strong> Schritten ({anteil} %) · {job.ideen} {job.ideen === 1 ? 'Idee' : 'Ideen'} ·{' '}
              {job.platforms.map((p) => PLATTFORM_NAME[p]).join(', ')}
            </p>
            {laufend && job.aktuell && (
              <p className="text-sm text-secondary" aria-live="polite">
                Gerade: „{job.aktuell.titel}“ ({PLATTFORM_NAME[job.aktuell.platform]})
              </p>
            )}
            {laufend && job.abbruchAngefordert && (
              <p className="text-sm text-muted">Wird nach dem laufenden Schritt beendet…</p>
            )}

            {job.status === 'kontofehler' && (
              <div className="alert alert-error" role="alert">{job.meldung || 'Die KI ist gerade nicht nutzbar.'}</div>
            )}
            {!laufend && (
              <div className={`alert ${job.status === 'fertig' && job.failed.length === 0 ? 'alert-success' : 'alert-info'}`} role="status">
                {job.status === 'fertig' ? 'Fertig' : job.status === 'abgebrochen' ? 'Abgebrochen' : 'Beendet'}:{' '}
                {job.erzeugt} {job.erzeugt === 1 ? 'Fassung' : 'Fassungen'} erzeugt, {job.skipped} übersprungen (schon vorhanden)
                {job.failed.length > 0 ? `, ${job.failed.length} fehlgeschlagen` : ''}.
                {job.status !== 'fertig' && job.status !== 'kontofehler' && job.meldung ? ` ${job.meldung}` : ''}
                {job.status !== 'fertig' ? ' Mit „nur fehlende“ neu starten setzt dort fort.' : ''}
              </div>
            )}

            {job.failed.length > 0 && (
              <details open={!laufend}>
                <summary>{job.failed.length} Fehler</summary>
                <ul className="social-stapel__fehler">
                  {job.failed.map((f) => (
                    <li key={`${f.postIdeaId}-${f.platform}`}>
                      <strong>{f.titel}</strong> ({PLATTFORM_NAME[f.platform]}): {f.meldung}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="btn-group mt-4">
              {laufend ? (
                <button type="button" className="btn btn-danger" onClick={abbrechen} disabled={job.abbruchAngefordert}>Durchlauf abbrechen</button>
              ) : (
                <button type="button" className="btn btn-secondary" onClick={() => { setJob(null); setHinweis(''); setFehler(''); }}>Neuer Durchlauf</button>
              )}
              <button type="button" className="btn btn-secondary" onClick={onClose}>{laufend ? 'Im Hintergrund weiterlaufen lassen' : 'Schließen'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialStapelDialog;
